import { FiX } from 'react-icons/fi'

import { useDashboard } from '@/hooks'
import { convertCelsius, tempUnitSuffix, userToCelsius } from '@/utils/conversions'

import { useTrackerMapContext } from './TrackerMapContext'

const BLUE = '#3b82f6'
const GREEN = '#22c55e'
const RED = '#ef4444'

// Native range input styled to a bare thumb (transparent track) so multiple can
// overlay one shared colored bar. Tailwind arbitrary variants style the thumb.
const THUMB_BASE =
  'pointer-events-none absolute inset-x-0 top-0 m-0 h-6 w-full cursor-pointer appearance-none bg-transparent ' +
  '[&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 ' +
  '[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full ' +
  '[&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow ' +
  '[&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 ' +
  '[&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 ' +
  '[&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow [&::-moz-range-track]:bg-transparent'

const THUMB_BLUE = '[&::-webkit-slider-thumb]:bg-blue-500 [&::-moz-range-thumb]:bg-blue-500'
const THUMB_RED = '[&::-webkit-slider-thumb]:bg-red-500 [&::-moz-range-thumb]:bg-red-500'

const Dot = ({ color }: { color: string }) => (
  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
)

/**
 * Floating temperature-threshold slider for history mode (bottom-center of the
 * map, mirroring the asset map Timeline). Operates in the user's display units.
 *
 * Two boundaries drive the point colors: a low (blue-below) handle and a high
 * (red-above) handle, either of which may be absent. With both present it is a
 * dual-handle range (blue / green / red); with one it's a single handle.
 */
export const TrackerHistorySlider = () => {
  const context = useTrackerMapContext()
  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'

  if (!context.historyMode) return null

  const suffix = tempUnitSuffix(units)
  const range = context.historyRangeC
  const noLocation = context.historyPoints != null && context.historyPoints.length === 0

  const close = (
    <button
      onClick={() => context.setHistoryMode(false)}
      className="btn btn-ghost btn-xs btn-square"
      aria-label="Exit history mode"
      title="Exit history"
    >
      <FiX className="w-3.5 h-3.5" />
    </button>
  )

  const shell = (body: React.ReactNode) => (
    <div className="absolute bottom-3 left-0 right-0 flex justify-center z-20 pointer-events-none">
      <div className="bg-base-100 rounded-lg shadow-lg p-3 pointer-events-auto w-full max-w-[460px] mx-4 border border-base-300">
        {body}
      </div>
    </div>
  )

  if (context.historyLoading) {
    return shell(<div className="py-2 text-center text-xs text-base-content/60">Loading history…</div>)
  }
  if (noLocation) {
    return shell(
      <div className="py-2 text-center text-xs text-base-content/60">No location history for this shipment</div>
    )
  }
  if (!range) {
    return shell(
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Temperature</span>
        {close}
      </div>
    )
  }

  const lowVal = context.historyLowC != null ? Math.round(convertCelsius(context.historyLowC, units)) : null
  const highVal = context.historyHighC != null ? Math.round(convertCelsius(context.historyHighC, units)) : null

  // Track bounds cover the observed data and both handles, padded to whole degrees.
  const candidates = [convertCelsius(range.min, units), convertCelsius(range.max, units)]
  if (lowVal != null) candidates.push(lowVal)
  if (highVal != null) candidates.push(highVal)
  const boundsMin = Math.floor(Math.min(...candidates))
  let boundsMax = Math.ceil(Math.max(...candidates))
  if (boundsMin === boundsMax) boundsMax = boundsMin + 1

  const pct = (v: number) => ((v - boundsMin) / (boundsMax - boundsMin)) * 100

  // Colored track: blue below low, green in range, red above high.
  const stops: string[] = []
  const pLow = lowVal != null ? pct(lowVal) : null
  const pHigh = highVal != null ? pct(highVal) : null
  if (pLow != null) stops.push(`${BLUE} 0%`, `${BLUE} ${pLow}%`, `${GREEN} ${pLow}%`)
  else stops.push(`${GREEN} 0%`)
  if (pHigh != null) stops.push(`${GREEN} ${pHigh}%`, `${RED} ${pHigh}%`, `${RED} 100%`)
  else stops.push(`${GREEN} 100%`)
  const trackBg = `linear-gradient(to right, ${stops.join(', ')})`

  const setLow = (displayVal: number) => {
    const clamped = Math.min(Math.max(displayVal, boundsMin), highVal ?? boundsMax)
    context.setHistoryLowC(userToCelsius(clamped, 1, units))
  }
  const setHigh = (displayVal: number) => {
    const clamped = Math.max(Math.min(displayVal, boundsMax), lowVal ?? boundsMin)
    context.setHistoryHighC(userToCelsius(clamped, 1, units))
  }

  return shell(
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Temperature</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tabular-nums flex items-center gap-1.5">
            {lowVal != null && (
              <span style={{ color: BLUE }}>
                {lowVal}
                {suffix}
              </span>
            )}
            {lowVal != null && highVal != null && <span className="text-base-content/40">–</span>}
            {highVal != null && (
              <span style={{ color: RED }}>
                {highVal}
                {suffix}
              </span>
            )}
          </span>
          {close}
        </div>
      </div>

      {/* Shared track with 1–2 overlaid range thumbs. */}
      <div className="relative h-6">
        <div
          className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full"
          style={{ background: trackBg }}
        />
        {lowVal != null && (
          <input
            type="range"
            aria-label="Minimum temperature"
            min={boundsMin}
            max={boundsMax}
            step={1}
            value={lowVal}
            onChange={(e) => setLow(Number(e.target.value))}
            className={`z-20 ${THUMB_BASE} ${THUMB_BLUE}`}
          />
        )}
        {highVal != null && (
          <input
            type="range"
            aria-label="Maximum temperature"
            min={boundsMin}
            max={boundsMax}
            step={1}
            value={highVal}
            onChange={(e) => setHigh(Number(e.target.value))}
            className={`z-10 ${THUMB_BASE} ${THUMB_RED}`}
          />
        )}
      </div>

      <div className="flex items-center justify-between mt-1 text-[11px] text-base-content/60">
        <span>
          {boundsMin}
          {suffix}
        </span>
        <div className="flex items-center gap-3">
          {lowVal != null && (
            <span className="flex items-center gap-1">
              <Dot color={BLUE} />
              below
            </span>
          )}
          <span className="flex items-center gap-1">
            <Dot color={GREEN} />
            {lowVal != null && highVal != null ? 'in range' : lowVal != null ? 'at or above' : 'below'}
          </span>
          {highVal != null && (
            <span className="flex items-center gap-1">
              <Dot color={RED} />
              {lowVal != null ? 'above' : 'at or above'}
            </span>
          )}
        </div>
        <span>
          {boundsMax}
          {suffix}
        </span>
      </div>
    </>
  )
}
